import { Block } from "@/types/editor/blocks";
import { characterShapes, Characters } from "@/config/font";
import { PATTERN_WIDTH_MM, PATTERN_HEIGHT_MM } from "./utils";

export interface RenderContext {
  patternSvgRef: HTMLImageElement | null;
  rootBlock: Block;
  SCALE: number;
  patternLoaded: boolean;
  debugMode: boolean;
  calculateRect: () => { x: number; y: number; width: number; height: number };
  baseMaterial: { color: string };
  useNaturalWidth: boolean;
  getNaturalWidthRatio: (character: string) => number;
  characterWidth?: number;
  characterHeight?: number;
  gap: number | number[];
  global_gap: number;
  width: number;
  height: number;
  showPattern?: boolean;
}

export interface RenderOverlayOptions {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  overlayImageUrl: string;
  backgroundImageUrl: string;
  setImagesLoading: (state: { overlay: boolean; background: boolean }) => void;
  tempOverlayRef: React.MutableRefObject<HTMLImageElement | null>;
  tempBackgroundRef: React.MutableRefObject<HTMLImageElement | null>;
  setHasUpdated: (updated: boolean) => void;
  render: () => void;
}

export const renderWithOverlay = async (options: RenderOverlayOptions) => {
  const {
    canvasRef,
    overlayImageUrl,
    backgroundImageUrl,
    setImagesLoading,
    tempOverlayRef,
    tempBackgroundRef,
    setHasUpdated,
    render,
  } = options;

  try {
    // Set loading state to true to show loading indicator
    setImagesLoading({
      overlay: !!overlayImageUrl,
      background: !!backgroundImageUrl,
    });

    // Track loaded images to avoid race conditions
    let tempOverlay = null;
    let tempBackground = null;

    // Helper function to load an image with proper promise handling
    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        // Set a reasonable timeout for image loading
        const timeoutId = setTimeout(() => {
          reject(new Error(`Image load timeout: ${url}`));
        }, 10000); // 10 second timeout

        img.onload = () => {
          clearTimeout(timeoutId);
          resolve(img);
        };

        img.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error(`Failed to load image: ${url}`));
        };

        img.src = url;
      });
    };

    // Load images in parallel with proper error handling
    const imagePromises: Promise<void>[] = [];

    if (overlayImageUrl) {
      imagePromises.push(
        loadImage(overlayImageUrl)
          .then((img) => {
            tempOverlay = img;
          })
          .catch((err) => {
            console.error(err);
            // Continue even if overlay fails
          })
      );
    }

    if (backgroundImageUrl) {
      imagePromises.push(
        loadImage(backgroundImageUrl)
          .then((img) => {
            tempBackground = img;
          })
          .catch((err) => {
            console.error(err);
            // Continue even if background fails
          })
      );
    }

    // Wait for all image loading attempts to complete
    await Promise.all(imagePromises);

    // Reset loading state
    setImagesLoading({
      overlay: false,
      background: false,
    });

    // Store the image references
    tempOverlayRef.current = tempOverlay;
    tempBackgroundRef.current = tempBackground;

    // Set the exporting flag to true
    if (canvasRef.current) {
      (
        canvasRef.current as HTMLCanvasElement & { isExporting: boolean }
      ).isExporting = true;
    }

    // Force a hasUpdated to ensure the render happens
    setHasUpdated(true);

    // Wait a frame to ensure everything is ready
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Force a render with the overlay and/or background
    render();

    // Wait for render to complete with a more robust delay
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Return a cleanup function
    const cleanup = () => {
      tempOverlayRef.current = null;
      tempBackgroundRef.current = null;

      // Reset the exporting flag
      if (canvasRef.current) {
        (
          canvasRef.current as HTMLCanvasElement & { isExporting: boolean }
        ).isExporting = false;
      }

      // Force a final render
      setHasUpdated(true);
      requestAnimationFrame(() => {
        render();
      });
    };

    return cleanup;
  } catch (error) {
    console.error("Error in renderWithOverlay:", error);
    // Reset all states on error
    setImagesLoading({
      overlay: false,
      background: false,
    });
    tempOverlayRef.current = null;
    tempBackgroundRef.current = null;
    if (canvasRef.current) {
      (
        canvasRef.current as HTMLCanvasElement & { isExporting: boolean }
      ).isExporting = false;
    }
    return undefined;
  }
};

export const renderPattern = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  context: RenderContext
) => {
  const { patternSvgRef, rootBlock, SCALE, debugMode, calculateRect } = context;

  if (!patternSvgRef) return;

  // Always use the root block dimensions for pattern calculation, regardless of which container is being rendered
  // This ensures all patterns have the same scale and alignment throughout the design
  const rootWidthMM = rootBlock?.width || width / SCALE;
  const rootHeightMM = rootBlock?.height || height / SCALE;

  // Calculate pattern repetitions based on the root container
  const patternRepetitions = calculateExactPatternRepetitions(
    rootWidthMM,
    rootHeightMM
  );

  // Check if we need to debug pattern repetition for large containers (like 555mm)
  const isLargeContainer = rootWidthMM >= 500;

  // Number of whole patterns to render (ceil for complete coverage)
  const patternsX = patternRepetitions.ceil.x;
  const patternsY = patternRepetitions.ceil.y;

  // Calculate pattern dimensions in pixels based on root container scale
  const rootScaleX = width / (rootWidthMM * SCALE);
  const rootScaleY = height / (rootHeightMM * SCALE);

  // Calculate pattern size in pixels using the root container's scale
  // Each pattern cell will have the same size regardless of which container it's in
  const patternWidthPx = PATTERN_WIDTH_MM * SCALE * rootScaleX;
  const patternHeightPx = PATTERN_HEIGHT_MM * SCALE * rootScaleY;

  // Calculate the offset for the pattern in this specific container
  // This ensures the pattern aligns with the root container's pattern grid
  const rootRect = calculateRect();
  const relativeX = (x - rootRect.x) / rootScaleX / SCALE; // Position in mm relative to root container
  const relativeY = (y - rootRect.y) / rootScaleY / SCALE;

  // Calculate pattern offset to ensure alignment with root container
  const patternOffsetX = -(relativeX % PATTERN_WIDTH_MM) * SCALE * rootScaleX;
  const patternOffsetY = -(relativeY % PATTERN_HEIGHT_MM) * SCALE * rootScaleY;

  // Create pattern
  const pattern = ctx.createPattern(patternSvgRef, "repeat");
  if (!pattern) return;

  // Save transform state
  ctx.save();

  // Apply scaling to pattern (using root container's scale)
  const matrix = new DOMMatrix();
  matrix.translateSelf(patternOffsetX, patternOffsetY);
  matrix.scaleSelf(
    patternWidthPx / patternSvgRef.width,
    patternHeightPx / patternSvgRef.height
  );
  pattern.setTransform(matrix);

  // Fill with pattern
  ctx.fillStyle = pattern;
  ctx.fillRect(x, y, width, height);

  // Debug: Draw grid lines to visualize pattern repetitions
  if (debugMode) {
    // Draw alternating background color for each pattern cell to make repetition clearer
    for (let i = 0; i < patternsX; i++) {
      for (let j = 0; j < patternsY; j++) {
        if ((i + j) % 2 === 0) continue; // Skip every other cell for checkerboard effect

        ctx.fillStyle = "rgba(255, 255, 0, 0.1)"; // Very light yellow highlight
        ctx.fillRect(
          x + i * patternWidthPx + patternOffsetX,
          y + j * patternHeightPx + patternOffsetY,
          patternWidthPx,
          patternHeightPx
        );
      }
    }

    // Draw grid lines
    ctx.strokeStyle = "rgba(255, 0, 0, 0.3)";
    ctx.lineWidth = 1;

    // Draw vertical lines
    for (let i = 0; i <= patternsX; i++) {
      const lineX = x + i * patternWidthPx + patternOffsetX;
      if (lineX >= x && lineX <= x + width) {
        ctx.beginPath();
        ctx.moveTo(lineX, y);
        ctx.lineTo(lineX, y + height);
        ctx.stroke();

        // Add index labels for columns
        if (i < patternsX) {
          ctx.fillStyle = "rgba(255, 0, 0, 0.8)";
          ctx.font = "10px Arial";
          ctx.fillText(`${i}`, lineX + 2, y + 10);
        }
      }
    }

    // Draw horizontal lines
    for (let i = 0; i <= patternsY; i++) {
      const lineY = y + i * patternHeightPx + patternOffsetY;
      if (lineY >= y && lineY <= y + height) {
        ctx.beginPath();
        ctx.moveTo(x, lineY);
        ctx.lineTo(x + width, lineY);
        ctx.stroke();

        // Add index labels for rows
        if (i < patternsY) {
          ctx.fillStyle = "rgba(255, 0, 0, 0.8)";
          ctx.font = "10px Arial";
          ctx.fillText(`${i}`, x + 2, lineY + 10);
        }
      }
    }

    // Draw physical rulers for large containers
    if (isLargeContainer) {
      const rulerHeight = 20;
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(x, y + height + 5, width, rulerHeight);

      // Draw 100mm ticks
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.fillStyle = "white";
      ctx.font = "10px Arial";

      for (let mm = 0; mm <= rootWidthMM; mm += 100) {
        const tickX = x + (mm / rootWidthMM) * width;
        ctx.beginPath();
        ctx.moveTo(tickX, y + height + 5);
        ctx.lineTo(tickX, y + height + 15);
        ctx.stroke();
        ctx.fillText(`${mm}mm`, tickX - 15, y + height + 25);
      }

      // Highlight expected pattern boundaries - every 10 patterns
      for (let i = 0; i <= patternRepetitions.exact.x; i += 10) {
        const patternX = x + ((i * PATTERN_WIDTH_MM) / rootWidthMM) * width;
        ctx.strokeStyle = "rgba(255, 255, 0, 0.8)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(patternX, y);
        ctx.lineTo(patternX, y + height + 25);
        ctx.stroke();

        if (i % 10 === 0) {
          ctx.fillStyle = "yellow";
          ctx.fillText(`P${i}`, patternX + 2, y + height + 40);
        }
      }

      // Draw special markers for 70.64 repetitions (555mm width)
      if (Math.abs(rootWidthMM - 555) < 10) {
        const expectedFor555 = 555 / PATTERN_WIDTH_MM;
        const mark555 =
          x + ((expectedFor555 * PATTERN_WIDTH_MM) / rootWidthMM) * width;

        // Draw special marker for 555mm (70.64 repetitions)
        ctx.strokeStyle = "rgb(0, 255, 0)"; // Bright green
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(mark555, y - 10);
        ctx.lineTo(mark555, y + height + 30);
        ctx.stroke();

        ctx.fillStyle = "rgb(0, 255, 0)";
        ctx.font = "bold 12px Arial";
        ctx.fillText(`555mm = 70.64 patterns`, mark555 + 5, y - 5);
      }
    }
  }

  // Restore context state
  ctx.restore();
};

export const renderBlock = (
  ctx: CanvasRenderingContext2D,
  block: Block,
  x: number,
  y: number,
  width: number,
  height: number,
  context: RenderContext,
  depth: number = 0
) => {
  const {
    SCALE,
    patternLoaded,
    patternSvgRef,
    debugMode,
    baseMaterial,
    useNaturalWidth,
    getNaturalWidthRatio,
    characterWidth,
    characterHeight,
    gap,
    global_gap,
    showPattern,
  } = context;

  // Calculate the actual scale factor applied to this block for rendering
  // This will be used for scaling corner radius and gaps proportionally
  const scaleX = width / (block.width * SCALE);
  const scaleY = height / (block.height * SCALE);
  const renderScale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

  // Check if this should have a transparent background:
  // 1. Character containers (containers that directly contain character blocks)
  // 2. Individual character blocks themselves
  const shouldBeTransparent =
    (block.isContainer &&
      block.blocks &&
      block.blocks.some((childBlock) => childBlock.character !== undefined)) ||
    block.character !== undefined;

  // Draw background with material color or black, but skip for transparent blocks
  if (!shouldBeTransparent) {
    ctx.fillStyle = block.color || "#000000";

    // If corner radius is specified and block is a container, draw rounded rectangle
    if (block.cornerRadius && block.isContainer) {
      const radius = block.cornerRadius * SCALE * renderScale;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill();

      // Apply pattern ONLY if this is the root block and showPattern is true
      if (depth === 0 && patternLoaded && patternSvgRef && showPattern) {
        // Save context state before clipping
        ctx.save();

        // Create clipping path using rectangle
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.clip();

        // Draw pattern inside clipped area
        renderPattern(ctx, x, y, width, height, context);

        // Restore context state
        ctx.restore();
      }
    } else {
      // Draw regular rectangle for non-container blocks or blocks without corner radius
      ctx.fillRect(x, y, width, height);

      // Apply pattern ONLY if this is the root block and showPattern is true
      if (depth === 0 && patternLoaded && patternSvgRef && showPattern) {
        // Save context state before clipping
        ctx.save();

        // Create clipping path using rectangle
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.clip();

        // Draw pattern inside clipped area
        renderPattern(ctx, x, y, width, height, context);

        // Restore context state
        ctx.restore();
      }
    }
  } else {
    // For transparent blocks (character containers and individual characters),
    // still apply pattern if this is the root block and showPattern is true
    // but skip the background fill to make it transparent
    if (depth === 0 && patternLoaded && patternSvgRef && showPattern) {
      // Save context state before clipping
      ctx.save();

      // Create clipping path using rectangle
      ctx.beginPath();
      ctx.rect(x, y, width, height);
      ctx.clip();

      // Draw pattern inside clipped area
      renderPattern(ctx, x, y, width, height, context);

      // Restore context state
      ctx.restore();
    }
  }

  // If this is a container, draw a border with a color based on depth
  if (block.isContainer && debugMode) {
    const containerColors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEEAD",
    ];

    ctx.strokeStyle = containerColors[depth % containerColors.length];
    ctx.lineWidth = 3;

    if (block.cornerRadius) {
      // Draw rounded border for debug mode using the same corrected scaling
      const radius = block.cornerRadius * SCALE * renderScale;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.stroke();
    } else {
      ctx.strokeRect(x, y, width, height);
    }
  }

  if (block.blocks && block.blocks.length > 0) {
    // Special handling for vertical stacked blocks
    if (block.verticalStack) {
      const totalBlocks = block.blocks.length;
      let currentY = y;

      // Calculate total vertical gaps with proper scaling
      const totalVerticalGap = block.blocks.reduce(
        (sum: number, b: Block, i: number) => {
          if (i < totalBlocks - 1) {
            return sum + (b.gap || 0);
          }
          return sum;
        },
        0
      );

      // Calculate available height for blocks with properly scaled gaps
      const availableHeight = height - totalVerticalGap * SCALE * renderScale;
      const blockHeight = availableHeight / totalBlocks;

      // Handle vertical stacking of characters
      block.blocks.forEach((childBlock: Block) => {
        // For vertical stack, width can be adjusted to maintain proportions
        let childWidth = width;
        const childHeight = blockHeight;

        // If useNaturalWidth is true, we'll center the character horizontally
        if (useNaturalWidth && childBlock.character) {
          const naturalRatio = getNaturalWidthRatio(childBlock.character);
          const characterWidth = childHeight * naturalRatio;
          // Center the character if it's smaller than the container
          if (characterWidth < width) {
            const horizontalPadding = (width - characterWidth) / 2;
            childWidth = characterWidth;
            renderBlock(
              ctx,
              childBlock,
              x + horizontalPadding,
              currentY,
              childWidth,
              childHeight,
              context,
              depth + 1
            );
          } else {
            // If character would be wider than container, use container width
            renderBlock(
              ctx,
              childBlock,
              x,
              currentY,
              width,
              childHeight,
              context,
              depth + 1
            );
          }
        } else {
          // Render at full width if not using natural width
          renderBlock(
            ctx,
            childBlock,
            x,
            currentY,
            width,
            childHeight,
            context,
            depth + 1
          );
        }

        // Move down for next character, adding vertical gap if needed with proper scaling
        currentY += childHeight + (childBlock.gap || 0) * SCALE * renderScale;
      });

      return; // Exit early since we've handled all children
    }

    // Regular horizontal layout
    const isVertical = width < height;
    const availableSpace = isVertical ? height : width;

    // Separate blocks by type for proper distribution
    const regularBlocks = block.blocks.filter(
      (b: Block) => !b.isContainer || (b.isContainer && !b.verticalStack)
    );
    const stackedContainers = block.blocks.filter(
      (b: Block) => b.isContainer && b.verticalStack
    );

    // Count effective blocks for distribution (each stacked container counts as one block)
    const effectiveBlockCount = regularBlocks.length + stackedContainers.length;

    // Calculate total gaps using the gap array if available with proper scaling
    const totalGaps = block.blocks.reduce(
      (sum: number, b: Block, i: number) => {
        if (i < block.blocks!.length - 1) {
          // Use the gap value directly from the block if available,
          // otherwise fallback to gap array or global gap
          const gapValue =
            b.gap !== undefined
              ? b.gap
              : Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : b.gapLocked
                  ? global_gap
                  : 0;

          return sum + gapValue;
        }
        return sum;
      },
      0
    );

    const spaceForBlocks = availableSpace - totalGaps * SCALE * renderScale;

    // Calculate total natural ratio if needed
    const totalNaturalRatio =
      useNaturalWidth && !isVertical
        ? block.blocks.reduce((sum: number, b: Block) => {
            if (b.isContainer) {
              if (b.verticalStack) {
                // For stacked containers, get the wider character's natural ratio
                if (b.blocks && b.blocks.length >= 2) {
                  const firstRatio = getNaturalWidthRatio(
                    b.blocks[0].character || ""
                  );
                  const secondRatio = getNaturalWidthRatio(
                    b.blocks[1].character || ""
                  );
                  return sum + Math.max(firstRatio, secondRatio);
                }
              }
              return sum + 1;
            }
            return sum + getNaturalWidthRatio(b.character || "");
          }, 0)
        : effectiveBlockCount;

    let currentX = x;
    let currentY = y;

    // Process all blocks
    for (let index = 0; index < block.blocks.length; index++) {
      const childBlock = block.blocks[index];
      let childX: number;
      let childY: number;
      let childWidth: number;
      let childHeight: number;

      // Handle stacked containers specifically
      if (childBlock.isContainer && childBlock.verticalStack) {
        if (isVertical) {
          // For vertical layouts, stacked containers take full width
          childWidth = width;
          // Calculate height based on position in container
          const blockSize = spaceForBlocks / effectiveBlockCount;
          childHeight = blockSize;
          childX = x;
          childY = currentY;
          currentY += childHeight;
        } else {
          // For horizontal layouts, calculate width based on distribution
          if (useNaturalWidth) {
            // Get natural ratio for the stacked container
            let stackRatio = 1;
            if (childBlock.blocks && childBlock.blocks.length > 0) {
              // Use max ratio of any characters in the stack
              stackRatio = childBlock.blocks.reduce(
                (max: number, stackedChild: Block) => {
                  if (stackedChild.character) {
                    const charRatio = getNaturalWidthRatio(
                      stackedChild.character
                    );
                    return Math.max(max, charRatio);
                  }
                  return max;
                },
                0.5
              );
            }
            childWidth = (spaceForBlocks * stackRatio) / totalNaturalRatio;
          } else {
            // Uniform distribution
            childWidth = spaceForBlocks / effectiveBlockCount;
          }
          childHeight = height;
          childX = currentX;
          childY = y;
        }

        // Render the stacked container
        renderBlock(
          ctx,
          childBlock,
          childX,
          childY,
          childWidth,
          childHeight,
          context,
          depth + 1
        );

        // Update currentX position for next block
        if (!isVertical) {
          currentX += childWidth;
        }

        // Add gap after this block if it's not the last one
        if (index < block.blocks.length - 1) {
          // For stacked containers, use the gap stored on the stacked container itself
          // This ensures the gap slider directly controls the space after the stacked container
          const gapValue =
            childBlock.gap !== undefined
              ? childBlock.gap
              : Array.isArray(gap) && index < gap.length
                ? gap[index]
                : childBlock.gapLocked
                  ? global_gap
                  : 0;

          if (isVertical) {
            currentY += gapValue * SCALE * renderScale;
          } else {
            currentX += gapValue * SCALE * renderScale;
          }
        }

        continue; // Skip to next block since we've handled this one
      }

      if (childBlock.isContainer) {
        // Use container's actual dimensions
        childWidth = (childBlock.width / block.width) * width;
        childHeight = (childBlock.height / block.height) * height;

        // Center the container
        const blocks = block.blocks;
        const allocatedWidth = isVertical ? width : width / blocks.length;
        const allocatedHeight = isVertical ? height / blocks.length : height;

        // Calculate total gaps before this block with proper scaling
        const gapsBefore = blocks
          .slice(0, index)
          .reduce((sum: number, _: Block, i: number) => {
            const gapValue =
              Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : blocks[i].gapLocked
                  ? global_gap
                  : (blocks[i].gap ?? 0);
            return sum + gapValue;
          }, 0);

        const allocatedX = isVertical
          ? x
          : x + index * allocatedWidth + gapsBefore * SCALE * renderScale;
        const allocatedY = isVertical
          ? y + index * allocatedHeight + gapsBefore * SCALE * renderScale
          : y;

        childX = allocatedX + (allocatedWidth - childWidth) / 2;
        childY = allocatedY + (allocatedHeight - childHeight) / 2;
      } else {
        // For character blocks
        if (useNaturalWidth && !isVertical) {
          // Calculate width based on natural ratio
          const naturalRatio = getNaturalWidthRatio(childBlock.character || "");

          // Calculate proportional width based on natural ratio
          // Ensure blockWidth doesn't go negative when gaps consume all space
          const blockWidth = Math.max(
            0,
            (spaceForBlocks * naturalRatio) / totalNaturalRatio
          );

          // If this is a character container (last block), use characterWidth and characterHeight
          if (characterWidth && characterHeight && !childBlock.blocks) {
            const charScale = Math.min(
              width / (characterWidth * SCALE),
              height / (characterHeight * SCALE)
            );
            childWidth = characterWidth * SCALE * charScale;
            childHeight = characterHeight * SCALE * charScale;

            // Center the character in its allocated space
            childX = currentX + (blockWidth - childWidth) / 2;
            childY = y + (height - childHeight) / 2;
          } else {
            childX = currentX;
            childY = y;
            childWidth = blockWidth;
            childHeight = height;
          }

          // Update currentX for next character
          currentX += blockWidth;

          // Add gap after this character if it's not the last one
          if (index < block.blocks.length - 1) {
            // Use the gap stored directly on the character block
            // This ensures the gap slider directly controls the space after this character
            let gapValue = childBlock.gap ?? global_gap;

            // Only fallback to gap array if no gap is set on the block
            if (
              childBlock.gap === undefined &&
              Array.isArray(gap) &&
              index < gap.length
            ) {
              gapValue = gap[index];
            }

            // Add the gap with proper scaling
            currentX += gapValue * SCALE * renderScale;
          }
        } else {
          // Use uniform distribution
          const blocks = block.blocks;
          const nonStackedBlocks = blocks.filter(
            (b: Block) => !b.isContainer || (b.isContainer && !b.verticalStack)
          ).length;
          const stackedBlocks = blocks.filter(
            (b: Block) => b.isContainer && b.verticalStack
          ).length;

          // Total effective blocks for sizing
          const effectiveCount = nonStackedBlocks + stackedBlocks;
          // Ensure blockSize doesn't go negative when gaps consume all space
          const blockSize = Math.max(0, spaceForBlocks / effectiveCount);

          if (isVertical) {
            childX = x;
            childY = currentY;

            // For vertical layouts, if using natural width, calculate width based on character ratio
            if (useNaturalWidth && childBlock.character) {
              childHeight = blockSize;
              const naturalRatio = getNaturalWidthRatio(childBlock.character);
              // Calculate width based on natural ratio (height * ratio) to maintain proportions
              const naturalWidth = childHeight * naturalRatio;

              // If natural width is less than container width, center it horizontally
              if (naturalWidth < width) {
                childWidth = naturalWidth;
                // Center the character horizontally
                childX = x + (width - childWidth) / 2;
              } else {
                // If natural width would exceed container width, cap it
                childWidth = width;
              }
            } else {
              // Default behavior for non-natural width mode
              childWidth = width;
              childHeight = blockSize;
            }

            currentY += blockSize;
            if (index < blocks.length - 1) {
              // Use the gap stored directly on the character block
              const gapValue =
                childBlock.gap !== undefined
                  ? childBlock.gap
                  : Array.isArray(gap) && index < gap.length
                    ? gap[index]
                    : childBlock.gapLocked
                      ? global_gap
                      : 0;

              currentY += gapValue * SCALE * renderScale;
            }
          } else {
            childX = currentX;
            childY = y;
            childWidth = blockSize;
            childHeight = height;
            currentX += blockSize;
            if (index < blocks.length - 1) {
              // Use the gap stored directly on the character block
              const gapValue =
                childBlock.gap !== undefined
                  ? childBlock.gap
                  : Array.isArray(gap) && index < gap.length
                    ? gap[index]
                    : childBlock.gapLocked
                      ? global_gap
                      : 0;

              currentX += gapValue * SCALE * renderScale;
            }
          }
        }
      }

      renderBlock(
        ctx,
        childBlock,
        childX,
        childY,
        childWidth,
        childHeight,
        context,
        depth + 1
      );
    }
  } else if (block.character) {
    // Set text color based on background
    const textColor = baseMaterial
      ? block.color === "#000000"
        ? baseMaterial.color // Black background gets base material color text
        : block.color === baseMaterial.color
          ? "#000000" // Base material background gets black text
          : "#ffffff" // Any other color gets white text
      : "#ffffff"; // Default to white if no base material

    // Special handling for dot and dash characters
    if (
      block.character === "." ||
      block.character === "-" ||
      block.character === " "
    ) {
      if (block.character === ".") {
        const [, , vbWidth, vbHeight] = characterShapes.dot.viewBox
          .split(" ")
          .map(Number);
        const dotHeight = height * 0.8;
        const dotScale = dotHeight / vbHeight;
        const dotWidth = vbWidth * dotScale;

        ctx.fillStyle = textColor;
        const path = new Path2D(characterShapes.dot.path);

        ctx.save();
        ctx.translate(x + (width - dotWidth) / 2, y + height);
        ctx.scale(dotScale, -dotScale);
        ctx.fill(path);
        ctx.restore();
      } else if (block.character === "-") {
        const [, , vbWidth, vbHeight] = characterShapes.dash.viewBox
          .split(" ")
          .map(Number);
        const dashWidth = width * 0.75;
        const dashHeight = height;
        const scaleX = dashWidth / vbWidth;
        const scaleY = dashHeight / vbHeight;

        ctx.fillStyle = textColor;
        const path = new Path2D(characterShapes.dash.path);

        ctx.save();
        ctx.translate(
          x + (width - dashWidth) / 2,
          y + height / 2 - dashHeight / 12
        );
        ctx.scale(scaleX, scaleY);
        ctx.fill(path);
        ctx.restore();
      } else if (block.character === " ") {
        // Space characters should be empty - no need to draw anything
      }
    } else {
      const blockKey = block.character.toLowerCase() as Characters;
      const shape = characterShapes[blockKey];

      if (shape) {
        const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
        const path = new Path2D(shape.path);

        // Ensure minimum render dimensions to prevent negative scaling
        const renderWidth = Math.max(1, width); // At least 1 pixel wide
        const renderHeight = Math.max(1, height); // At least 1 pixel high

        ctx.save();
        ctx.translate(x, y);
        ctx.scale(renderWidth / vbWidth, renderHeight / vbHeight);
        ctx.fillStyle = textColor;
        ctx.fill(path);
        ctx.restore();
      }
    }
  }
};

// Helper function used by the pattern renderer
function calculateExactPatternRepetitions(widthMM: number, heightMM: number) {
  // Calculate exact number of patterns that should fit
  const exactPatternsX = widthMM / PATTERN_WIDTH_MM;
  const exactPatternsY = heightMM / PATTERN_HEIGHT_MM;

  return {
    exact: {
      x: exactPatternsX,
      y: exactPatternsY,
    },
    // For visual rendering, we need to ceil to cover the entire area
    ceil: {
      x: Math.ceil(exactPatternsX),
      y: Math.ceil(exactPatternsY),
    },
    // Calculate pattern size needed for exact repetition
    patternSize: {
      widthMM: widthMM / exactPatternsX, // This will be the same as PATTERN_WIDTH_MM
      heightMM: heightMM / exactPatternsY, // This will be the same as PATTERN_HEIGHT_MM
    },
  };
}
